import { Box, Flex, Grid, Heading } from "@sidelinesports/fan-design-system";
import { StarIcon } from "@sidelinesports/icons";
import type { ApiScoringAndRankInfo } from "../../../../api/scoring-and-rank.ts";
import { Dialog } from "../../../../components/index.tsx";
import { UploadedImage } from "../../../../components/UploadedImage.tsx";
import { useNumberFormatter } from "../../../../hooks/use-number-formatter.ts";
import { useOrdinalFormatter } from "../../../../hooks/use-ordinal-formatter.ts";
import { useCurrentGameQuery } from "../../hooks/use-current-game.ts";
import { useLeaderboard } from "./hooks/use-leaderboard.ts";
import styles from "./LeaderboardDialog.module.css";

export const LeaderboardDialog = () => {
  const leaderboardQuery = useLeaderboard();

  if (leaderboardQuery.isPending) {
    return (
      <Dialog.Content name="Leaderboard" size="fullscreen">
        <Dialog.Header>Leaderboard</Dialog.Header>
      </Dialog.Content>
    );
  }

  if (leaderboardQuery.isError) {
    return (
      <Dialog.Content name="Leaderboard" size="fullscreen">
        <Dialog.Header>Leaderboard</Dialog.Header>
      </Dialog.Content>
    );
  }

  return (
    <Dialog.Content name="Leaderboard" size="fullscreen">
      <Dialog.Header>Leaderboard</Dialog.Header>
      <Dialog.Body>
        <LeaderboardInterior data={leaderboardQuery.data} />
      </Dialog.Body>
    </Dialog.Content>
  );
};

const LeaderboardInterior = ({
  data: { rank, leaders },
}: {
  data: ApiScoringAndRankInfo;
}) => {
  const toIntegerString = useNumberFormatter();
  const toOrdinal = useOrdinalFormatter();

  const hasLeaders = leaders.length > 0 && leaders[0].score > 0;
  const userRank = hasLeaders ? rank.rank : null;

  return (
    <Grid gap="1rem">
      <Box p="1rem" textAlign="center">
        <Heading
          as="div"
          level={2}
          aria-label={`You are ranked ${rank.rank ? toOrdinal(rank.rank) : "unranked"}`}
          className={styles["rank-ordinal"]}
        >
          {userRank ? toOrdinal(userRank) : "—"}
        </Heading>
      </Box>
      <LeaderboardSponsor />
      <Box background="primary" className={styles["leaderboard-list"]}>
        {leaders.length === 0 && (
          <Box p="1rem" textAlign="center">
            Check back soon!
          </Box>
        )}
        {leaders.map((leader) =>
          times(leader.count, (index) => {
            const isUserRow = leader.rank === userRank && index === 0;
            const isUserTied = leader.rank === userRank && leader.count > 1;

            return (
              <div
                key={`${leader.rank}-${index}`}
                className={styles["leaderboard-item"]}
                data-owner={isUserRow ? "self" : undefined}
              >
                <div className={styles["leaderboard-rank"]}>{leader.rank}</div>
                <div className={styles["leaderboard-message"]}>
                  {isUserRow
                    ? isUserTied
                      ? `You’re tied for ${toOrdinal(leader.rank)}!`
                      : "You’re here!"
                    : ""}
                </div>
                <div className={styles["leaderboard-points"]}>
                  {isUserRow && (
                    <div className={styles["own-row-indicator"]}>
                      <StarIcon />
                    </div>
                  )}
                  {toIntegerString(leader.score)}
                </div>
              </div>
            );
          }),
        )}
      </Box>
    </Grid>
  );
};

const LeaderboardSponsor = () => {
  const gameQuery = useCurrentGameQuery();

  const sponsor = gameQuery.data?.gameSponsor;

  if (!sponsor) {
    return null;
  }

  return (
    <Flex align="center" justify="center" py={24}>
      <UploadedImage
        image={sponsor.image}
        alt="Game sponsor"
        maxWidth="12rem"
        objectFit="contain"
        tracking={{
          location: "Leaderboard::SponsorLogo",
        }}
      />
    </Flex>
  );
};

function times(n: number, fn: (index: number) => React.ReactNode) {
  return Array.from({ length: n }, (_, i) => fn(i));
}
